// @flow
import React from 'react';
import type { Node } from 'react';

import ResidentService from '../../services/residentService';
import useIntl from '../../hooks/useIntl';
import messages from './messages';
import {
  CertificationImpactRow,
  ImpactList,
  PreviewStatusView,
  countImpactRecords,
  useCorrectionPreview,
} from '../EditMoveDateModal/correctionImpacts';
import type {
  DateFieldValue,
  PreviewCert,
  PreviewWarning,
  StatusChangeHandler,
} from '../EditMoveDateModal/correctionImpacts';

type PropertyMoveInCorrectionImpactsData = {
  currentPropertyMoveInDate: ?string,
  newPropertyMoveInDate: string,
  /**
   * The completed certs whose form prints the Property Move-In Date (flagged, never regenerated),
   * and the open certs whose generated form prints it (that form must be generated again).
   */
  certifications: Array<{ ...PreviewCert, program: string }>,
  warnings: Array<PreviewWarning>,
};

type Props = {
  residentId: string,
  organizationId: string,
  propertyId: string,
  /** The date field's value; only previewed once the client rules pass. */
  proposedDate: DateFieldValue,
  /** YYYY-MM-DD; an unchanged date has nothing to preview. */
  currentPropertyMoveInDate: ?string,
  /** The form's own validation (required, format, future, fiscal period) passed. */
  clientValid: boolean,
  /**
   * The status, with the YYYY-MM-DD date it answers (null when there is none) and the number of
   * records listed (0 unless valid).
   */
  onStatusChange: StatusChangeHandler,
};

/** The record-count header; it names the modal's scrolling impact region (aria-labelledby). */
export const PROPERTY_MOVE_IN_IMPACTS_COUNT_ID = 'pmi-impacts-count-label';

/** The affected-record list of a valid Property Move-In Date correction preview. */
const PropertyMoveInImpactList = ({
  impacts,
}: {
  impacts: ?PropertyMoveInCorrectionImpactsData,
}): Node => {
  const intl = useIntl();
  const recordCount = countImpactRecords(impacts);
  if (!impacts || !recordCount) {
    return null;
  }

  return (
    <ImpactList
      testIdPrefix="pmi"
      countId={PROPERTY_MOVE_IN_IMPACTS_COUNT_ID}
      reviewBanner={intl.formatMessage(messages.reviewBanner)}
      recordCount={recordCount}
      warnings={impacts.warnings}
    >
      {impacts.certifications.map((cert) => (
        <CertificationImpactRow
          key={cert.id}
          cert={cert}
          dataTest="pmi-impact-cert"
          needsReviewDescription={intl.formatMessage(
            messages.needsReviewDescription,
            { formName: cert.formName },
          )}
          regenerateFormDescription={intl.formatMessage(
            messages.regenerateFormDescription,
            { formName: cert.formName },
          )}
        />
      ))}
    </ImpactList>
  );
};

/**
 * The affected-record list of the Edit Property Move-In Date modal (household-mi-mo-date-changes).
 * Once the entered date passes the form's rules it asks the backend for the dry-run plan of that
 * date and lists the completed certifications a Save would flag for review, and the open ones whose
 * generated form it would void. Reports its status so the modal can gate Save on it. Its previews
 * are dropped when it unmounts (the modal closed or saved), so a reopened modal asks again.
 */
const PropertyMoveInCorrectionImpacts = ({
  residentId,
  organizationId,
  propertyId,
  proposedDate,
  currentPropertyMoveInDate,
  clientValid,
  onStatusChange,
}: Props): Node => {
  const { status, impacts, message, retry } =
    useCorrectionPreview<PropertyMoveInCorrectionImpactsData>({
      queryKey: [
        'propertyMoveInDateCorrectionPreview',
        organizationId,
        propertyId,
        residentId,
      ],
      proposedDate,
      currentDate: currentPropertyMoveInDate,
      clientValid,
      fetchPreview: (date, signal) =>
        new ResidentService().previewPropertyMoveInDateCorrection(
          residentId,
          organizationId,
          propertyId,
          date,
          signal,
        ),
      onStatusChange,
    });

  if (status !== 'valid') {
    return (
      <PreviewStatusView
        testIdPrefix="pmi"
        status={status}
        message={message}
        onRetry={retry}
      />
    );
  }
  return <PropertyMoveInImpactList impacts={impacts} />;
};

export default PropertyMoveInCorrectionImpacts;
